import React, { memo } from "react";

import equal from "fast-deep-equal/es6";
import * as Highcharts from "highcharts";
import "highcharts/modules/drilldown";
import { HighchartsReact } from "highcharts-react-official";

import { colorTheme } from "@utils";

import Error from "./Common/Error";
import Header from "./Common/Header";
import Loading from "./Common/Loading";
import { Wrapper } from "./Common/styles";

type Series = {
  name: string;
  id?: string;
  data: number[] | [string, number][] | Record<string, string | number>[];
  showInLegend?: boolean;
  color?: string;
  borderColor?: string;
  borderWidth?: number;
}[];

type BarChartProps = {
  title: string;
  subtitle?: string;
  type?: "bar" | "stacked" | "histogram" | "distribution";
  yAxisLabel: string;
  xAxisLabel?: string;
  maxNumber?: number;
  options?: string[];
  highlightTooltipSuffix?: string;
  data: Series;
  linkUrl?: string;
  linkText?: string;
  toolTip?: string;
  loading?: boolean;
  error?: string | null;
  drillDown?: Series;
  formatter?: Highcharts.TooltipFormatterCallbackFunction;
  hideXAxisLabels?: boolean;
  onClick?: (point: Highcharts.Point) => void;
};

const BarChart = memo(
  ({
    title,
    subtitle,
    type = "bar",
    yAxisLabel,
    xAxisLabel,
    options,
    maxNumber,
    highlightTooltipSuffix,
    data,
    linkUrl,
    linkText,
    toolTip,
    loading,
    error,
    drillDown,
    formatter,
    hideXAxisLabels,
    onClick,
  }: BarChartProps) => {
    const renderBarChart = () => {
      if (error) {
        return <Error errorMessage={error} />;
      }

      if (loading) {
        return <Loading />;
      }

      let distributionMaxNumber = 0;
      if (type === "distribution") {
        distributionMaxNumber = Math.max(
          ...data.map((o) => Math.max(...(o.data as number[]))),
        );
      }

      const barOptions: Highcharts.Options = {
        chart: {
          type: type === "distribution" ? "bar" : "column",
        },
        yAxis:
          type === "distribution"
            ? [
                {
                  title: {
                    text: "",
                  },
                  labels: {
                    format: `{value}${highlightTooltipSuffix || ""}`,
                  },
                  width: "50%",
                  reversed: true,
                  max: distributionMaxNumber,
                },
                {
                  title: {
                    text: "",
                  },
                  labels: {
                    format: `{value}${highlightTooltipSuffix || ""}`,
                  },
                  offset: 0,
                  left: "50%",
                  width: "50%",
                  max: distributionMaxNumber,
                },
              ]
            : {
                min: 0,
                max: maxNumber ?? undefined,
                title: {
                  text: yAxisLabel,
                },
              },
        tooltip: {
          valueSuffix: highlightTooltipSuffix,
          formatter:
            type === "distribution"
              ? function () {
                  return (
                    `<b>${this.series.name}, ${
                      xAxisLabel || "category"
                    } ${this.key}</b><br>` +
                    `${yAxisLabel}: ${this.y}${highlightTooltipSuffix || ""}`
                  );
                }
              : formatter,
        },
        plotOptions: {
          column: {
            pointPadding: type === "histogram" ? 0 : 0.05,
            groupPadding: type === "histogram" ? 0 : 0.1,
            stacking: type === "stacked" ? "normal" : undefined,
            borderWidth: 0,
            dataLabels: {
              enabled: true,
              format: `{point.y:,.0f}${highlightTooltipSuffix ?? ""}`,
              style: {
                fontSize: "13px",
              },
            },
            cursor: onClick ? "pointer" : undefined,
            events: {
              click: onClick ? (e) => onClick(e.point) : undefined,
            },
          },
          bar: {
            stacking: type === "distribution" ? "normal" : undefined,
            groupPadding: type === "distribution" ? 0.01 : 0.1,
            pointPadding: 0.05,
            borderWidth: 0,
            dataLabels: {
              enabled: type !== "distribution",
              format: `{point.y:,.0f}${highlightTooltipSuffix ?? ""}`,
              style: {
                fontSize: "13px",
              },
            },
            cursor: onClick ? "pointer" : undefined,
            events: {
              click: onClick ? (e) => onClick(e.point) : undefined,
            },
          },
        },
        series: data as Highcharts.SeriesBarOptions[],
        legend: {
          align: "left",
        },
        title: { text: undefined },
        credits: {
          enabled: false,
        },
      };

      if (options) {
        if (type === "distribution") {
          barOptions.xAxis = [
            {
              categories: options,
              reversed: false,
              labels: {
                step: 1,
              },
            },
            {
              opposite: true,
              reversed: false,
              categories: options,
              linkedTo: 0,
              labels: {
                step: 1,
              },
            },
          ];
        } else {
          barOptions.xAxis = { categories: options, crosshair: true };
        }
      } else {
        barOptions.xAxis = { type: "category", crosshair: true };
      }
      if (hideXAxisLabels) {
        if (Array.isArray(barOptions.xAxis)) {
          barOptions.xAxis[0].labels = { enabled: false };
          barOptions.xAxis[1].labels = { enabled: false };
        } else {
          barOptions.xAxis.labels = {
            enabled: false,
          };
        }
      }
      if (xAxisLabel && !Array.isArray(barOptions.xAxis)) {
        barOptions.xAxis.title = {
          text: xAxisLabel,
        };
      }

      if (drillDown) {
        barOptions.drilldown = {
          series: drillDown as Highcharts.SeriesBarOptions[],
          activeAxisLabelStyle: {
            color: colorTheme("neutralL1"),
          },
          activeDataLabelStyle: {
            color: colorTheme("neutralL1"),
            textDecoration: "none",
          },
        };
      }

      return <HighchartsReact highcharts={Highcharts} options={barOptions} />;
    };

    return (
      <Wrapper>
        <Header
          title={title}
          subtitle={subtitle}
          toolTip={toolTip}
          linkUrl={linkUrl}
          linkText={linkText}
        />
        {renderBarChart()}
      </Wrapper>
    );
  },
  (oldProps, newProps) => equal(oldProps, newProps),
);

export default BarChart;
